import { Link } from 'react-router-dom';
import { HybridIcon } from '../components/HybridIcon';
import './Landing.css';

export default function Landing() {
  const buildNumber = import.meta.env.VITE_BUILD_NUMBER || 'local-dev';

  const features = [
    {
      icon: 'car',
      title: 'Fleet Management',
      description: 'Track all your vehicles in one place. Monitor status, mileage, and important documents like insurance and fitness certificates.',
    },
    {
      icon: 'people',
      title: 'Customer Management',
      description: 'Maintain a comprehensive database of your customers with contact information and driver license details.',
    },
    {
      icon: 'key',
      title: 'Rental Tracking',
      description: 'Manage all your rentals with detailed tracking of start dates, end dates, payments, and customer information.',
    },
    {
      icon: 'construct',
      title: 'Service Logs',
      description: 'Keep track of vehicle maintenance with service logs, odometer readings, and automatic service reminders.',
    },
    {
      icon: 'shield-checkmark',
      title: 'Document Tracking',
      description: 'Never miss an expiration date. Get alerts for insurance, fitness certificates, and license renewals.',
    },
    {
      icon: 'people-circle',
      title: 'Multi-User Support',
      description: 'Work together with your team. Share access to your fleet data with role-based permissions for admins and members.',
    },
    {
      icon: 'analytics',
      title: 'Dashboard Overview',
      description: 'Get instant insights into your fleet with a comprehensive dashboard showing key metrics and upcoming actions.',
    },
    {
      icon: 'cloud',
      title: 'Cloud-Based',
      description: 'Access your fleet data from anywhere, on any device. Your data is securely stored in the cloud and always up-to-date.',
    },
  ];

  return (
    <div className="landing-container">
      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <div className="landing-hero-icon">
            <img 
              src="/pathFinderLogo.png" 
              alt="Path Finder Logo" 
              className="landing-hero-logo"
            />
          </div>
          <h1 className="landing-hero-title">Path Finder</h1>
          <p className="landing-hero-subtitle">Fleet Management Made Simple</p>
          <p className="landing-hero-description">
            Streamline your vehicle rental business with comprehensive fleet management, 
            customer tracking, and automated service reminders.
          </p>
          <p className="landing-build-number">Build {buildNumber}</p>
          <div className="landing-hero-actions">
            <Link to="/signup" className="landing-button landing-button-primary">
              Get Started Free
            </Link>
            <Link to="/login" className="landing-button landing-button-secondary">
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="landing-features">
        <div className="landing-section-content">
          <h2 className="landing-section-title">Everything You Need to Manage Your Fleet</h2>
          <p className="landing-section-subtitle">
            Powerful features designed to help you stay organized and grow your business
          </p>
          <div className="landing-features-grid">
            {features.map((feature, index) => (
              <div key={index} className="landing-feature-card">
                <div className="landing-feature-icon">
                  <HybridIcon name={feature.icon} size={40} color="#f4511e" />
                </div>
                <h3 className="landing-feature-title">{feature.title}</h3>
                <p className="landing-feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="landing-benefits">
        <div className="landing-section-content">
          <h2 className="landing-section-title">Why Choose Path Finder?</h2>
          <div className="landing-benefits-list">
            <div className="landing-benefit-item">
              <HybridIcon name="checkmark-circle" size={32} color="#4caf50" />
              <div>
                <h3>Save Time</h3>
                <p>Automate reminders and track everything in one place. No more spreadsheets or manual tracking.</p>
              </div>
            </div>
            <div className="landing-benefit-item">
              <HybridIcon name="checkmark-circle" size={32} color="#4caf50" />
              <div>
                <h3>Stay Compliant</h3>
                <p>Never miss an expiration date. Get alerts for insurance, fitness certificates, and license renewals.</p>
              </div>
            </div>
            <div className="landing-benefit-item">
              <HybridIcon name="checkmark-circle" size={32} color="#4caf50" />
              <div>
                <h3>Team Collaboration</h3>
                <p>Work together with your team. Share access with role-based permissions for seamless collaboration.</p>
              </div>
            </div>
            <div className="landing-benefit-item">
              <HybridIcon name="checkmark-circle" size={32} color="#4caf50" />
              <div>
                <h3>Real-Time Updates</h3>
                <p>All your data syncs in real-time. Make changes on any device and see updates instantly everywhere.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-cta">
        <div className="landing-section-content">
          <h2 className="landing-cta-title">Ready to Get Started?</h2>
          <p className="landing-cta-subtitle">
            Join Path Finder today and take control of your fleet management
          </p>
          <div className="landing-cta-actions">
            <Link to="/signup" className="landing-button landing-button-primary landing-button-large">
              Create Free Account
            </Link>
            <Link to="/login" className="landing-button landing-button-secondary landing-button-large">
              Sign In to Existing Account
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-section-content">
          <div className="landing-footer-content">
            <div className="landing-footer-brand">
              <HybridIcon name="car" size={32} color="#f4511e" />
              <span className="landing-footer-name">Path Finder</span>
            </div>
            <div className="landing-footer-links">
              <Link to="/login" className="landing-footer-link">Sign In</Link>
              <Link to="/signup" className="landing-footer-link">Sign Up</Link>
              <Link to="/help" className="landing-footer-link">Help</Link>
            </div>
          </div>
          <div className="landing-footer-copyright">
            <p>&copy; {new Date().getFullYear()} Path Finder. All rights reserved.</p>
            <p className="landing-build-number-footer">Build {buildNumber}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

